import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { mitErsatz } from "@/office/makro/pruefung";
import { readDocument } from "@/office/word/document";
import { readZeichen } from "@/office/word/zeichen";
import { MAKRO_DOKUMENT } from "../makros";

interface Dekoration {
  mitRahmen: number;
  mitSchattierung: number;
}

async function dekorationLesen(): Promise<Dekoration | null> {
  const zeichen = await readZeichen();
  if (!zeichen.absaetze) return null;
  const gefuellt = zeichen.absaetze.filter((absatz) => absatz.text.length > 0);
  return {
    mitRahmen: gefuellt.filter((absatz) => absatz.hatRahmen).length,
    mitSchattierung: gefuellt.filter((absatz) => absatz.schattierung !== null).length,
  };
}

/** Absätze, die eine Linie aus getippten Zeichen sind. */
async function getippteLinien(): Promise<number> {
  const snapshot = await readDocument();
  return snapshot.filledParagraphs.filter((absatz) => /^[-_=*~]{5,}$/.test(absatz.text.replace(/\s/g, "")))
    .length;
}

export const lektionRahmen: Lesson = {
  id: "rahmen-schattierung",
  title: "Rahmen und Schattierung",
  summary: "Einen Hinweis sichtbar absetzen – ohne Tabelle mit einer Zelle und ohne getippte Linien.",
  durationMinutes: 8,
  learningGoals: [
    "Einen Absatz mit Rahmenlinien versehen",
    "Eine Schattierung als Hintergrund eines Absatzes setzen",
    "Getippte Linien und Ein-Zellen-Tabellen als Behelfslösung erkennen",
  ],
  steps: [
    {
      id: "absatzrahmen",
      title: "Rahmenlinien an einem Absatz",
      explanation: [
        "Ein Hinweiskasten, eine Trennlinie über der Unterschrift, ein Strich unter dem " +
          "Briefkopf – all das sind Rahmenlinien eines Absatzes. Du findest sie auf der " +
          "Registerkarte „Start“ neben der Schattierung, beim kleinen Pfeil an der " +
          "Schaltfläche „Rahmenlinien“.",
        "Ganz unten in diesem Menü steht „Rahmen und Schattierung …“. Dort lassen sich " +
          "Linienart, Farbe und Breite wählen und die Seiten einzeln an- und abschalten. " +
          "Unter „Optionen“ steht der Abstand zwischen Linie und Text.",
        "Zwei Behelfslösungen begegnen einem oft: eine Tabelle mit einer einzigen Zelle, " +
          "nur damit ein Kasten entsteht, und eine Reihe getippter Unterstriche oder " +
          "Bindestriche als Linie. Beide zerfallen, sobald sich der Text verschiebt oder die " +
          "Seitenbreite ändert. Ein Absatzrahmen wächst mit dem Absatz mit.",
      ],
      keyPoints: [
        "Start → Rahmenlinien → Rahmen und Schattierung.",
        "Der Rahmen gehört zum Absatz und wächst mit ihm.",
        "Keine Ein-Zellen-Tabellen und keine getippten Linien.",
      ],
      task: {
        instruction:
          "Schreibe einen kurzen Hinweis-Absatz (etwa „Wichtig: Abgabe bis Freitag“) und gib " +
          "ihm einen Rahmen auf allen vier Seiten.",
        hint: "Cursor in den Absatz, Start → Rahmenlinien → Rahmenlinien außen.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: async () => {
          const linien = await getippteLinien();
          return mitErsatz(
            dekorationLesen,
            MAKRO_DOKUMENT,
            (bericht) => ({
              mitRahmen: bericht.zahl("absaetzeMitRahmen") ?? 0,
              mitSchattierung: bericht.zahl("absaetzeMitSchattierung") ?? 0,
            }),
            ({ mitRahmen }) =>
              fromCriteria(
                [
                  criterion("Ein Absatz hat Rahmenlinien", mitRahmen > 0),
                  criterion(
                    `Keine getippten Linien aus Strichen oder Unterstrichen (gefunden: ${linien})`,
                    linien === 0,
                  ),
                ],
                {
                  success: "Der Hinweis ist abgesetzt – und der Rahmen wächst mit dem Text mit.",
                  failure: "Der Rahmen fehlt noch oder es steht eine getippte Linie im Dokument.",
                  hint: "Getippte Linien löschen und stattdessen eine untere Rahmenlinie verwenden.",
                },
              ),
          );
        },
      },
    },
    {
      id: "schattierung",
      title: "Schattierung",
      explanation: [
        "Die Schattierung ist die Hintergrundfarbe eines Absatzes. Die Schaltfläche mit dem " +
          "Farbeimer auf der Registerkarte „Start“ färbt den ganzen Absatz, wenn der Cursor " +
          "nur darin steht, und nur den markierten Text, wenn etwas markiert ist.",
        "Zusammen mit einem Rahmen ergibt das den klassischen Hinweiskasten. Wähle einen " +
          "hellen Ton aus der oberen Zeile der Designfarben: Dunkler Hintergrund macht " +
          "schwarzen Text schwer lesbar – dieselbe Kontrastfrage wie im Modul " +
          "„Schriftarten“.",
        "Nicht verwechseln: Die Texthervorhebung (der Textmarker) ist ein Zeichenformat " +
          "zum Markieren beim Lesen. Die Schattierung ist Gestaltung. Wer beides mischt, " +
          "weiß nach einer Woche nicht mehr, was nur markiert und was gestaltet war.",
      ],
      keyPoints: [
        "Start → Schattierung (Farbeimer) färbt den Absatzhintergrund.",
        "Helle Farben aus der oberen Zeile der Designfarben.",
        "Schattierung ist Gestaltung, der Textmarker ist eine Lesemarkierung.",
      ],
      task: {
        instruction: "Gib deinem Hinweis-Absatz zusätzlich eine helle Schattierung.",
        hint: "Cursor in den Absatz (nichts markieren), dann Start → Schattierung.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: () =>
          mitErsatz(
            dekorationLesen,
            MAKRO_DOKUMENT,
            (bericht) => ({
              mitRahmen: bericht.zahl("absaetzeMitRahmen") ?? 0,
              mitSchattierung: bericht.zahl("absaetzeMitSchattierung") ?? 0,
            }),
            ({ mitRahmen, mitSchattierung }) =>
              fromCriteria(
                [
                  criterion("Ein Absatz hat eine Schattierung", mitSchattierung > 0),
                  criterion("Ein Absatz hat Rahmenlinien", mitRahmen > 0),
                ],
                {
                  success: "Rahmen und Schattierung – ein Hinweis, den niemand übersieht.",
                  failure: "Die Schattierung fehlt noch.",
                  hint: "Wenn nur einzelne Wörter farbig sind, war Text markiert. Cursor nur in den Absatz setzen.",
                },
              ),
          ),
      },
    },
    {
      id: "als-vorlage",
      title: "Den Hinweiskasten als Formatvorlage",
      explanation: [
        "Rahmen und Schattierung sind Absatzformate – und gehören damit, wie Abstände und " +
          "Schriftgrößen, in eine Formatvorlage. Markiere deinen fertigen Hinweis und wähle " +
          "im Formatvorlagen-Katalog „Formatvorlage erstellen“, etwa unter dem Namen " +
          "„Hinweis“.",
        "Ab dann ist jeder weitere Hinweiskasten ein Klick. Und wenn die Hausfarbe sich " +
          "ändert, passt du die Vorlage einmal an statt jeden Kasten einzeln.",
      ],
      keyPoints: [
        "Wiederkehrende Kästen als eigene Formatvorlage anlegen.",
        "Eine Änderung an der Vorlage ändert alle Kästen gleichzeitig.",
      ],
    },
  ],
};
